import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";

import { useChatSessionStateStore, usePersistedStore, useSocketStateStore } from "@asurion-core/headless-chat-web";
import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useNotification } from "@expert/common-ui";
import { useEffect, useMemo, useRef } from "react";

import type { GuideSessionContext, OpenGuideSource } from "../types";

import {
    getGuideStepSummary,
    GuidedFlowMessageMapper,
    type LogicBuilderGlobalConfig,
    useSimplrInitializers,
} from "../logic-builder";
import { useGuideSession, useGuideStore } from "../store";
import { useGuideDisclosure } from "./useGuideDisclosure";
import { useParseMessages } from "./useParseMessages";

export interface GuideManagerProps {
    // Session/task
    sessionId: string;
    callSid?: string;
    employeeId?: string;
    isCustomerSession?: boolean;

    // Simplr config
    globalConfig?: LogicBuilderGlobalConfig;

    // Other
    analytics?: Record<string, unknown>;
    clientChannelId?: string;
    logger: Logger;
    partner: Partner;
    sendMessage: SendExpertAssistMessage;
    onAskAssist?: () => void;
    ssoAccessToken: string;
    taskMdn: string;
    isSolveGuidedFlowsEnabled: boolean;
    apiBaseUrl: string;
}

interface GuideToolboxCommand {
    open: (source: OpenGuideSource) => void;
    tooltip?: string;
    quickActionLabel?: string;
    disabled: boolean;
}

export function useGuideManager({
    callSid,
    clientChannelId,
    employeeId,
    globalConfig,
    logger: loggerProp,
    partner,
    sendMessage,
    onAskAssist,
    sessionId,
    ssoAccessToken,
    taskMdn,
    isSolveGuidedFlowsEnabled,
    apiBaseUrl,
}: GuideManagerProps) {
    const platformTicketId = useChatSessionStateStore((state) => state.platformTicketId);

    const logger = useMemo(() => loggerProp.child({ module: "useGuideManager" }), [loggerProp]);

    useEffect(() => {
        logger.info({ sessionId, platformTicketId }, "Solve Guide | Session Updated");
    }, [sessionId, platformTicketId, logger]);

    useSimplrInitializers({
        globalConfig,
        logger,
        sessionId,
    });

    const connectionState = useSocketStateStore((state) => state.connectionState);
    // The single global socket is bound to one chat at a time; activeExternalTicketId is that
    // chat's id (the app starts sessions with externalTicketId === sessionId). Used below to
    // confirm a ticketEnded actually belongs to this session before stamping.
    const activeExternalTicketId = usePersistedStore((state) => state.activeExternalTicketId);
    const notification = useNotification();

    const {
        analyticsInfo: { currentStepId },
        availableGuides,
        isLoadingSimplrSession,
    } = useGuideSession(sessionId);
    const areGuidesAvailable = availableGuides.length > 0 && isSolveGuidedFlowsEnabled;

    const { latestMessage, messages } = useParseMessages({ employeeId, sessionId });
    const guideStep = useMemo(() => getGuideStepSummary(latestMessage), [latestMessage]);
    const { isGuideOpen, openGuide, closeGuide } = useGuideDisclosure({
        callSid,
        logger,
        platformTicketId,
        sessionId,
    });

    // Seeded with the current value so mount is never treated as a transition. This hook is a
    // singleton bound to the selected session (it re-renders, never remounts, on chat switch), and
    // connectionState is a single global, latched socket value. Stamping on a mount that lands while
    // the global already reads "ticketEnded" would flag a freshly-selected healthy session, the exact
    // cross-session contamination this guard prevents. Only a genuine transition (below) stamps.
    const previousConnectionStateRef = useRef(connectionState);
    useEffect(() => {
        const previousConnectionState = previousConnectionStateRef.current;
        previousConnectionStateRef.current = connectionState;

        const { setIsErrorState, setErrorStateSessionId, getGuideSession } = useGuideStore.getState();
        if (connectionState === "ticketEnded") {
            // Stamp only on the real transition into ticketEnded (not the latched value across a
            // switch, see the ref comment) AND only when the socket that ended is bound to THIS
            // session. During a chat switch the outgoing chat can end before startSession re-points
            // the socket, firing a real connected->ticketEnded transition while a new healthy chat
            // is already selected; correlating the ended ticket avoids restarting that healthy chat.
            if (previousConnectionState !== "ticketEnded" && activeExternalTicketId === sessionId) {
                logger.info("Solve Guide | Ticket Ended event received");
                setIsErrorState(sessionId, true);
                // Only seed once per error; re-entry would otherwise keep appending `_RESTARTED`.
                if (!getGuideSession(sessionId).errorStateSessionId) {
                    setErrorStateSessionId(sessionId);
                }
            }
        } else setIsErrorState(sessionId, false);
    }, [connectionState, sessionId, activeExternalTicketId, logger]);

    useEffect(() => {
        if (platformTicketId && currentStepId !== "" && isLoadingSimplrSession === true) {
            logger.info(
                { currentStepId, platformTicketId },
                "Solve Guide | platformTicketId updated and first step loaded, turning off loading state",
            );
            useGuideStore.getState().setIsLoadingSimplrSession(sessionId, false);
        }
    }, [currentStepId, isLoadingSimplrSession, platformTicketId, sessionId, logger]);

    const session = useMemo<GuideSessionContext>(
        () => ({ apiBaseUrl, callSid, clientChannelId, partner, sessionId, ssoAccessToken, taskMdn }),
        [apiBaseUrl, callSid, clientChannelId, partner, sessionId, ssoAccessToken, taskMdn],
    );

    const ActiveGuide = areGuidesAvailable ? (
        <GuidedFlowMessageMapper
            latestMessage={latestMessage}
            logger={logger}
            onGuideClose={closeGuide}
            onAskAssist={onAskAssist}
            opened={isGuideOpen}
            sendMessage={sendMessage}
            session={session}
        />
    ) : null;

    const handleOpenGuideFailure = (source: OpenGuideSource) => {
        void sendErrorMetric(`openGuideFailure`);
        // TODO: @asurion-core/expert-workspace-solve: we should not see this error
        logger.warn(
            { source, connectionState, globalConfig, messages: JSON.stringify(messages) },
            "Solve guide | No active guide found, trigger error toast",
        );
        notification.error("We're having trouble launching the guide—try again.");
    };

    return {
        areGuidesAvailable,
        guide: ActiveGuide,
        guideStep,
        guideToolboxCommand: {
            open: ActiveGuide ? openGuide : handleOpenGuideFailure,
            tooltip: !areGuidesAvailable ? "Coming soon" : undefined,
            quickActionLabel: areGuidesAvailable ? "/g" : undefined,
            disabled: !areGuidesAvailable,
        } as GuideToolboxCommand,
        isGuideOpen,
        closeGuide,
        openGuide,
    };
}
